import type { DbObject } from "@/db/schema"
import { db, ObjectType } from "@/db/schema"

/**
 * Check if a task has meaningful data beyond just a title.
 *
 * Used for the escape-guard pattern (§31): when the user presses Escape
 * on a newly created task with an empty title, the task should only be
 * deleted if it has NO meaningful data. If it has tags, userStatus,
 * notes, checklist items, or a deadline — keep it even without a title.
 *
 * Note: `startAt` is NOT considered meaningful because it's often set
 * automatically by the view (e.g., Today view sets startAt to today).
 *
 * This function is async because it needs to check for checklist children
 * in the database.
 */
export async function hasMeaningfulData(task: DbObject): Promise<boolean> {
  // UserStatus set
  if (task.userStatus !== undefined && task.userStatus !== null) {
    return true
  }

  // Has tags
  if (task.tags && task.tags.length > 0) {
    return true
  }

  // Has deadline (dueAt)
  if (task.dueAt) {
    return true
  }

  // Has notes (doc Y.Doc or note text)
  if (task.doc) {
    return true
  }
  if (task.note && task.note.trim().length > 0) {
    return true
  }

  // Has checklist children
  if (task.id) {
    const checklistCount = await db.totodo
      .where("[parentPath+type]")
      .equals([`${task.parentPath}/${task.id}`, ObjectType.Checklist])
      .filter((item) => item.isDeleted === 0)
      .count()

    if (checklistCount > 0) {
      return true
    }
  }

  return false
}

/** Maximum age (ms) for a task to be considered "newly created". */
export const NEWLY_CREATED_WINDOW = 60_000

/**
 * Check if a task was recently created (within NEWLY_CREATED_WINDOW).
 */
export function isNewlyCreated(task: DbObject): boolean {
  if (!task.createdAt) return false
  const createdMs =
    typeof task.createdAt === "string"
      ? parseInt(task.createdAt, 10)
      : task.createdAt
  return Date.now() - createdMs < NEWLY_CREATED_WINDOW
}
